import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, RawButton, RotateDocumentIcon, ZoomInIcon, ZoomOutIcon } from '@common-fe/common-fe';
import styled from 'styled-components';

interface ToolbarProps {
  $floating?: boolean;
}

const Toolbar = styled(Box)<ToolbarProps>`
  ${({ $floating }) =>
    $floating &&
    `
    position: absolute;
    bottom: 22px;
    left: 50%;
    transform: translateX(-50%);
  `}
  z-index: 1;
  pointer-events: all;
  white-space: nowrap;
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.border1};
  opacity: 0.1;
  flex-shrink: 0;
  margin: 0 2px;
`;

interface ToolbarButtonProps {
  $disabled?: boolean;
}

const ToolbarButton = styled(RawButton)<ToolbarButtonProps>`
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.rounds.checkboxRound};
  width: 36px;
  height: 36px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  flex-shrink: 0;
  transition: background 0.15s;
  box-sizing: border-box;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    background: ${({ $disabled }) => ($disabled ? 'transparent' : 'rgba(255, 255, 255, 0.1)')};
  }
  svg {
    display: block;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    opacity: ${({ $disabled }) => ($disabled ? 0.2 : 1)};
    transition: opacity 0.15s;
  }
`;

interface Props {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotate: () => void;
  floating?: boolean;
  isZoomInDisabled?: boolean;
  isZoomOutDisabled?: boolean;
}

const ZoomRotateToolbar: React.FC<Props> = ({
  onZoomIn,
  onZoomOut,
  onRotate,
  floating = true,
  isZoomInDisabled = false,
  isZoomOutDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <Toolbar
      direction="row"
      align="center"
      gap="2px"
      background="background3"
      round="container2Round"
      pad="spacing4"
      $floating={floating}
      aria-label={t('Document toolbar')}
    >
      <ToolbarButton
        aria-label={t('Zoom in')}
        title={t('Zoom in')}
        onClick={onZoomIn}
        $disabled={isZoomInDisabled}
        aria-disabled={isZoomInDisabled}
      >
        <ZoomInIcon color="textOnColor" />
      </ToolbarButton>

      <ToolbarButton
        aria-label={t('Zoom out')}
        title={t('Zoom out')}
        onClick={onZoomOut}
        $disabled={isZoomOutDisabled}
        aria-disabled={isZoomOutDisabled}
      >
        <ZoomOutIcon color="textOnColor" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton aria-label={t('Rotate document')} title={t('Rotate document')} onClick={onRotate}>
        <RotateDocumentIcon color="textOnColor" />
      </ToolbarButton>
    </Toolbar>
  );
};

export default ZoomRotateToolbar;
